import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { FraudFlag } from '@dcc-erp/shared';
import { FRAUD_DETECTION_TYPES } from '@dcc-erp/shared';

import { Can } from '../components/Can';
import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries';
import {
  downloadFraudReportPdf,
  useDismissFraudFlag,
  useFraudFlags,
  useResolveFraudFlag,
  useRunFraudScan,
} from '../lib/queries/fraud';

export const Route = createFileRoute('/fraud')({ component: FraudPage });

// Thai labels for the 7 locked detective report codes.
const TYPE_LABEL: Record<string, string> = {
  DUP_DISB: 'จ่ายซ้ำ (Duplicate disbursement)',
  NEAR_DUP: 'เลขที่ใบแจ้งหนี้ใกล้เคียงกัน (Fuzzy near-dup)',
  SPLIT_SUM: 'แยกบิลเลี่ยงเกณฑ์อนุมัติ (Split / running-sum)',
  PAY_BEFORE_APPROVAL: 'จ่ายก่อนอนุมัติ (Payment before approval)',
  MANUAL_JV_AP_TRADE: 'JV ตั้งเจ้าหนี้โดยไม่มีบิล (Manual JV to AP_TRADE)',
  APPROVAL_BYPASSED: 'ข้ามขั้นอนุมัติ (Approvals bypassed)',
  CROSS_VENDOR_TAXID: 'เลขผู้เสียภาษีซ้ำข้ามผู้ขาย (Cross-vendor tax-id)',
};

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'เปิดอยู่',
  RESOLVED: 'แก้ไขแล้ว',
  DISMISSED: 'ยกเลิก (ไม่ใช่ทุจริต)',
};

// Map INFO/WARNING/CRITICAL onto the prototype.css badge variants.
function severityBadge(sev: string): { cls: string; label: string } {
  if (sev === 'CRITICAL') return { cls: 'badge negative', label: 'วิกฤต' };
  if (sev === 'WARNING') return { cls: 'badge warning', label: 'เตือน' };
  return { cls: 'badge info', label: 'ข้อมูล' };
}

function statusBadge(status: string): string {
  if (status === 'RESOLVED') return 'badge positive';
  if (status === 'DISMISSED') return 'badge neutral';
  return 'badge warning';
}

function fmtDateTime(v: string | Date): string {
  const d = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type CaseAction = 'resolve' | 'dismiss';

export function FraudPage() {
  const companyId = useActiveCompanyId();

  const [statusFilter, setStatusFilter] = useState<string>('OPEN');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const filter = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(typeFilter ? { type: typeFilter } : {}),
    }),
    [statusFilter, typeFilter],
  );

  const { data: flags = [], isPending } = useFraudFlags(companyId, filter);
  const runScan = useRunFraudScan(companyId);
  const resolve = useResolveFraudFlag(companyId);
  const dismiss = useDismissFraudFlag(companyId);

  // The case whose comment modal is open + which action it commits.
  const [active, setActive] = useState<{ flag: FraudFlag; action: CaseAction } | null>(null);
  const [comment, setComment] = useState('');

  // Severity/status summary over the currently-loaded feed.
  const summary = useMemo(() => {
    const s = { CRITICAL: 0, WARNING: 0, INFO: 0, open: 0 };
    for (const f of flags) {
      if (f.severity === 'CRITICAL') s.CRITICAL += 1;
      else if (f.severity === 'WARNING') s.WARNING += 1;
      else s.INFO += 1;
      if (f.status === 'OPEN') s.open += 1;
    }
    return s;
  }, [flags]);

  async function handleScan() {
    try {
      await runScan.mutateAsync();
    } catch (e) {
      window.alert(`สแกนไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  function openModal(flag: FraudFlag, action: CaseAction) {
    setComment('');
    setActive({ flag, action });
  }

  async function handleConfirm() {
    if (!active) return;
    const payload = { id: active.flag.id, comment };
    try {
      if (active.action === 'resolve') await resolve.mutateAsync(payload);
      else await dismiss.mutateAsync(payload);
      setActive(null);
      setComment('');
    } catch (e) {
      window.alert(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  const modalTitle =
    active?.action === 'resolve' ? 'ทำเครื่องหมายว่าแก้ไขแล้ว' : 'ยกเลิกเคส (false positive)';
  const submitting = resolve.isPending || dismiss.isPending;

  return (
    <div className="page">
      <PageHead
        crumbs={['ควบคุมภายใน', 'ตรวจจับทุจริต (Anti-Fraud)']}
        title="แดชบอร์ดตรวจจับทุจริต (Fraud Detective)"
        actions={
          <Can permission="fraud.manage">
            <button
              className="btn primary"
              onClick={() => void handleScan()}
              disabled={!companyId || runScan.isPending}
            >
              {runScan.isPending ? 'กำลังสแกน…' : 'สแกนเดี๋ยวนี้'}
            </button>
          </Can>
        }
      />

      <div
        className="kpi-row"
        style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}
      >
        <div className="kpi">
          <div className="label">เคสที่เปิดอยู่</div>
          <div className="value">{summary.open}</div>
        </div>
        <div className="kpi">
          <div className="label">วิกฤต</div>
          <div className="value">{summary.CRITICAL}</div>
        </div>
        <div className="kpi">
          <div className="label">เตือน</div>
          <div className="value">{summary.WARNING}</div>
        </div>
        <div className="kpi">
          <div className="label">ข้อมูล</div>
          <div className="value">{summary.INFO}</div>
        </div>
      </div>

      <div className="filter-row">
        <label style={{ fontSize: 14 }}>สถานะ</label>
        <select
          className="select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ height: 36 }}
        >
          <option value="">ทั้งหมด</option>
          <option value="OPEN">เปิดอยู่</option>
          <option value="RESOLVED">แก้ไขแล้ว</option>
          <option value="DISMISSED">ยกเลิก</option>
        </select>

        <label style={{ fontSize: 14, marginLeft: 8 }}>ประเภท</label>
        <select
          className="select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ height: 36 }}
        >
          <option value="">ทุกรายงาน</option>
          {FRAUD_DETECTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABEL[t] ?? t}
            </option>
          ))}
        </select>

        <Can permission="fraud.read">
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FRAUD_DETECTION_TYPES.map((t) => (
              <button
                key={t}
                className="btn sm ghost"
                onClick={() => companyId && void downloadFraudReportPdf(companyId, t)}
                disabled={!companyId}
                title={TYPE_LABEL[t] ?? t}
              >
                ส่งออก PDF: {t}
              </button>
            ))}
          </div>
        </Can>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>ความรุนแรง</th>
              <th>ประเภท</th>
              <th>รายละเอียด</th>
              <th>รายการที่เกี่ยวข้อง</th>
              <th>พบครั้งแรก</th>
              <th>พบล่าสุด</th>
              <th>สถานะ</th>
              <th>การจัดการ</th>
            </tr>
          </thead>
          <tbody>
            {isPending && (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  กำลังโหลด…
                </td>
              </tr>
            )}
            {!isPending && flags.length === 0 && (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  ไม่พบเคสตามเงื่อนไข
                </td>
              </tr>
            )}
            {flags.map((f) => {
              const sb = severityBadge(f.severity);
              const related = [
                ...f.relatedBillIds.map((id) => `บิล ${id}`),
                ...f.relatedPaymentIds.map((id) => `จ่าย ${id}`),
                ...f.relatedJournalIds.map((id) => `JV ${id}`),
              ];
              return (
                <tr key={f.id}>
                  <td>
                    <span className={sb.cls}>{sb.label}</span>
                  </td>
                  <td>{TYPE_LABEL[f.detectionType] ?? f.detectionType}</td>
                  <td>{f.title}</td>
                  <td style={{ fontSize: 13 }}>{related.join(', ') || '—'}</td>
                  <td>{fmtDateTime(f.flaggedAt)}</td>
                  <td>{fmtDateTime(f.lastSeenAt)}</td>
                  <td>
                    <span className={statusBadge(f.status)}>
                      {STATUS_LABEL[f.status] ?? f.status}
                    </span>
                  </td>
                  <td>
                    {f.status === 'OPEN' ? (
                      <Can permission="fraud.manage">
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn sm" onClick={() => openModal(f, 'resolve')}>
                            แก้ไขแล้ว
                          </button>
                          <button className="btn sm ghost" onClick={() => openModal(f, 'dismiss')}>
                            ยกเลิก (false positive)
                          </button>
                        </div>
                      </Can>
                    ) : (
                      <span className="muted" style={{ fontSize: 13 }}>
                        {f.comment ?? '—'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {active && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,15,20,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setActive(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              width: 480,
              maxWidth: '100%',
              borderRadius: 10,
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
            }}
          >
            <h3 style={{ margin: 0 }}>{modalTitle}</h3>
            <div className="muted" style={{ fontSize: 13 }}>
              {active.flag.title}
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ความเห็น / เหตุผล
              </label>
              <textarea
                className="input"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="ระบุเหตุผล / ความเห็นการตรวจสอบ (บังคับ)"
                style={{ width: '100%', padding: 10, fontSize: 14, minHeight: 70 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn ghost" onClick={() => setActive(null)} disabled={submitting}>
                ยกเลิก
              </button>
              <button
                className="btn primary"
                onClick={() => void handleConfirm()}
                disabled={submitting || comment.trim().length === 0}
              >
                {submitting ? 'กำลังบันทึก…' : 'ยืนยัน'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
